<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  terms: {
    type: Array,
    default: () => [],
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['save']);

const { t } = useI18n();

const dialogRef = ref(null);
const localTerms = ref([]);
const newTerm = ref('');

const open = () => {
  localTerms.value = [...props.terms];
  newTerm.value = '';
  dialogRef.value.open();
};

const close = () => dialogRef.value.close();

const addTerm = () => {
  const term = newTerm.value.trim();
  if (!term) return;
  // Case-insensitive dedupe — backend matching already lowercases, so "API"
  // and "api" are the same protected term.
  const isDuplicate = localTerms.value.some(
    existing => existing.toLowerCase() === term.toLowerCase()
  );
  if (isDuplicate) return;
  localTerms.value.push(term);
  newTerm.value = '';
};

const removeTerm = index => localTerms.value.splice(index, 1);

const handleConfirm = () => emit('save', [...localTerms.value]);

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="xl"
    :title="t('SPELL_CHECK_SETTINGS.PROTECTED_TERMS.MODAL_TITLE')"
    :description="t('SPELL_CHECK_SETTINGS.PROTECTED_TERMS.MODAL_DESCRIPTION')"
    :confirm-button-label="t('SPELL_CHECK_SETTINGS.PROTECTED_TERMS.SAVE')"
    :cancel-button-label="t('SPELL_CHECK_SETTINGS.PROTECTED_TERMS.CANCEL')"
    :is-loading="isSaving"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-4">
      <!-- Add control -->
      <div class="flex items-center gap-2">
        <input
          v-model="newTerm"
          type="text"
          :placeholder="
            t('SPELL_CHECK_SETTINGS.PROTECTED_TERMS.INPUT_PLACEHOLDER')
          "
          class="h-10 min-w-0 flex-1 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-[13px] text-n-slate-12 outline-none transition-colors placeholder:text-n-slate-10 focus:border-n-brand"
          @keydown.enter.prevent="addTerm"
        />
        <button
          type="button"
          class="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-lg bg-n-brand px-4 text-[13px] font-semibold text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-60"
          :disabled="!newTerm.trim()"
          @click="addTerm"
        >
          <span class="i-lucide-plus size-4" />
          {{ t('SPELL_CHECK_SETTINGS.PROTECTED_TERMS.ADD') }}
        </button>
      </div>

      <!-- Terms table -->
      <div class="max-h-72 overflow-y-auto rounded-lg border border-n-weak">
        <table v-if="localTerms.length" class="w-full text-start">
          <tbody>
            <tr
              v-for="(term, index) in localTerms"
              :key="`${term}-${index}`"
              class="border-b border-n-weak last:border-b-0"
            >
              <td class="px-3 py-2.5 text-[13px] text-n-slate-12">
                {{ term }}
              </td>
              <td class="w-10 px-3 py-2.5 text-end">
                <button
                  type="button"
                  class="grid size-7 place-items-center rounded-md text-n-slate-11 transition-colors hover:bg-n-ruby-3 hover:text-n-ruby-11"
                  :aria-label="t('SPELL_CHECK_SETTINGS.PROTECTED_TERMS.REMOVE')"
                  @click="removeTerm(index)"
                >
                  <span class="i-lucide-x size-4" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="px-3 py-8 text-center text-[13px] text-n-slate-11">
          {{ t('SPELL_CHECK_SETTINGS.PROTECTED_TERMS.EMPTY') }}
        </p>
      </div>
    </div>
  </Dialog>
</template>
